import React from 'react'
import styled from 'styled-components'
import { useDemo } from '../DemoProvider'

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`

const Modal = styled.div`
  background: var(--color-background);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 600px;
  width: 100%;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary),
      var(--color-secondary)
    );
  }
`

const Header = styled.div`
  padding: 2rem;
  text-align: center;
`

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: 1.125rem;
  line-height: 1.6;
`

const Content = styled.div`
  padding: 0 2rem 2rem;
`

const FeatureList = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 2rem;
`

const Feature = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`

const FeatureIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(59, 130, 246, 0.1);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`

const FeatureContent = styled.div`
  h3 {
    color: var(--color-text);
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
    line-height: 1.6;
  }
`

const Actions = styled.div`
  padding: 2rem;
  background: rgba(255, 255, 255, 0.02);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
    color: white;
    
    &:hover {
      transform: translateY(-2px);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: var(--color-text);
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}
`

const WelcomeModal = ({ onClose }) => {
  const { exitDemoMode } = useDemo()

  return (
    <Overlay>
      <Modal>
        <Header>
          <Title>Welcome to Demo Mode</Title>
          <Subtitle>
            Experience the power of AI agents in a simulated environment
          </Subtitle>
        </Header>

        <Content>
          <FeatureList>
            <Feature>
              <FeatureIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </FeatureIcon>
              <FeatureContent>
                <h3>No Wallet Required</h3>
                <p>
                  Try out all platform features without connecting a wallet or making any transactions.
                </p>
              </FeatureContent>
            </Feature>

            <Feature>
              <FeatureIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
              </FeatureIcon>
              <FeatureContent>
                <h3>Pre-configured Projects</h3>
                <p>
                  Choose from template projects to see how different types of deployments work.
                </p>
              </FeatureContent>
            </Feature>

            <Feature>
              <FeatureIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </FeatureIcon>
              <FeatureContent>
                <h3>Real-time Simulation</h3>
                <p>
                  Watch AI agents collaborate in real-time with our interactive 3D visualization.
                </p>
              </FeatureContent>
            </Feature>
          </FeatureList>
        </Content>

        <Actions>
          <Button onClick={exitDemoMode}>Exit Demo Mode</Button>
          <Button $primary onClick={onClose}>Start Demo</Button>
        </Actions>
      </Modal>
    </Overlay>
  )
}

export default WelcomeModal
